'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconButton,
  Popover,
  Tooltip,
  toasterApi,
} from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { Key01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useApplicationCredentials } from '../../../hooks/useApplicationCredentials';
import { CredentialsMenuContent } from './CredentialsMenuContent';

type TopBarCredentialsMenuProps = {
  applicationId?: string;
  disabled?: boolean;
};

export function TopBarCredentialsMenu({
  applicationId,
  disabled,
}: TopBarCredentialsMenuProps) {
  const tMenu = useTranslations('layout.credentialsMenu');
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hasShownFetchErrorToastRef = useRef(false);
  const triggerLabel = tMenu('manageCredentials');

  const {
    credentials,
    isLoading,
    error: fetchError,
  } = useApplicationCredentials({
    applicationId,
    enabled: !!applicationId,
  });

  const enabledCredentials = useMemo(
    () => credentials.filter((credential) => !credential.isDisabled),
    [credentials],
  );

  const isDisabled = !!disabled || !applicationId;

  useEffect(() => {
    if (!isOpen) {
      hasShownFetchErrorToastRef.current = false;

      return;
    }

    if (isLoading || !fetchError || hasShownFetchErrorToastRef.current) {
      return;
    }

    hasShownFetchErrorToastRef.current = true;
    toasterApi.error({
      title: tMenu('fetchErrorTitle'),
      description: tMenu('fetchErrorDescription'),
    });
  }, [fetchError, isLoading, isOpen, tMenu]);

  return (
    <Box display="inline-flex" flexShrink={0}>
      <Popover.Root
        open={isOpen}
        onOpenChange={(details) => setIsOpen(details.open)}
        positioning={{
          placement: 'bottom-end',
          strategy: 'fixed',
          gutter: 8,
          getAnchorRect: () =>
            triggerRef.current?.getBoundingClientRect() ?? null,
        }}
        closeOnInteractOutside
        autoFocus={false}
        lazyMount
        unmountOnExit
      >
        <Tooltip title={triggerLabel}>
          <Popover.Trigger asChild>
            <IconButton
              ref={triggerRef}
              type="button"
              variant="ghost"
              size="xs"
              flexShrink={0}
              aria-label={triggerLabel}
              disabled={isDisabled}
            >
              <Key01Icon size={16} strokeWidth={1.75} />
            </IconButton>
          </Popover.Trigger>
        </Tooltip>

        <Popover.Positioner>
          <Popover.Content w="320px" p={2} overflow="hidden">
            <CredentialsMenuContent
              applicationId={applicationId}
              credentials={enabledCredentials}
              isLoading={isLoading}
              isMenuOpen={isOpen}
              onClose={() => setIsOpen(false)}
            />
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>
    </Box>
  );
}
